import React, { useEffect, useRef, useState } from "react"
import { useGLTF } from '@react-three/drei'
import { useControls } from 'leva'
import { useThree, useFrame } from "@react-three/fiber"
import { Mesh, Cache, Vector3, Box3, AxesHelper, LinearMipMapLinearFilter, LinearFilter } from 'three'
import { isDebugMode } from '../../../../../helpers/general'
import { fadeIn, fadeOut, plainHide, plainShow, setModelOpacity } from '../../../animations'

function hideOrShowWallBasedItem({ meshObject, assets, camera, animation, setAnimation, fadeDuration, hideAnimation = fadeOut, showAnimation = fadeIn }) {
  const [uuid] = meshObject.name?.split('_')

  if (assets && assets[uuid]?.category?.wall_based) {
    // Get mesh's local Y axis in world space
    const localYAxis = new Vector3(0, 1, 0)
      .applyMatrix4(meshObject.matrixWorld)
      .sub(meshObject.getWorldPosition(new Vector3()))
      .normalize()

    const cameraPosition = camera.position.clone()
    const objectPosition = meshObject.getWorldPosition(new Vector3())
    const cameraToObjectDirection = objectPosition.sub(cameraPosition).normalize()
    const dotProduct = localYAxis.dot(cameraToObjectDirection)

    if (dotProduct > 0) {
      hideAnimation({
        mesh: meshObject,
        animationState: animation,
        setAnimation: setAnimation,
        fadeDuration,
      })
    } else {
      showAnimation({
        mesh: meshObject,
        animationState: animation,
        setAnimation: setAnimation,
        fadeDuration,
      })
    }
  }
}

function tuneModelChildMeshObject(object, gl) {
  if (object.name.toLowerCase() === 'fp') {
    const texture = object.material.map
    texture.minFilter = LinearMipMapLinearFilter
    texture.magFilter = LinearFilter
    texture.anisotropy = gl.capabilities.getMaxAnisotropy()
    texture.needsUpdate = true
  }
  object.castShadow = true
  object.receiveShadow = true
  object.material.envMapIntensity = 200
}

export function InteriorModel({ url, onLoad, assets, rotationAngle, cameraConfig, isAnimateFromTopView, isVerticalRotationDone, setIsVerticalRotationDone }) {
  let lastCheck = 0
  const rotationVelocity = useRef(0)
  const position = [0,0,0]
  const [autoRotation, setAutoRotation] = useState(true)
  const [rotation, setRotation] = useState(isAnimateFromTopView ? [Math.PI/2,0,0] :[0,0,0])
  const [rotationAngleState, setRotationAngle] = useState(null)
  const modelRef = useRef(null)
  const checkInterval = 200
  const gltf = useGLTF(`${url}`)
  const { viewport, camera, gl } = useThree()
  const prevPositionRef = useRef(camera.position.clone())
  const debugEnabled = isDebugMode()
  const [animation, setAnimation] = useState('idle')
  const [isReadyRender, setIsReadyRender] = useState(false)
  const [isReadyAnimate, setIsReadyAnimate] = useState(false);

  const [,setBoundingBox] = useControls('Model', () => (
    {
      max: { value: [0,0,0], disabled: true },
      min: { value: [0,0,0], disabled: true },
      center: { value: [0,0,0], disabled: true },
    }
  ))

  Cache.clear()

  useEffect(() => {
    onLoad(gltf)
    const cameraDirection = new Vector3()
    camera.getWorldDirection(cameraDirection)  // Get the camera's direction in world space

    gltf.scene.position.set(...position)
    gltf.scene.rotation.set(...rotation)
    gltf.scene.updateMatrixWorld(true)
    gltf.scene.traverse((object) => {
      if (object instanceof Mesh) {
        if (object.material && object?.userData?.support_transparency) {
          object.userData['maxOpacity'] = object?.material?.opacity
        }

        // need this hack since objects from Blender are being exported each time with version prefix e.g 9d4396ae-b508-42cf-b8b4-0ead99fb16d004_1
        const [uuid] = object.name?.split('_')

        if (assets && assets[uuid]?.category?.wall_based) {
          hideOrShowWallBasedItem({
            meshObject: object,
            assets,
            camera,
            cameraDirection,
            hideAnimation: plainHide,
            showAnimation: plainShow,
          })
          if (debugEnabled) {
            const axesHelper = new AxesHelper(3)
            object.add(axesHelper)
          }
        }

        tuneModelChildMeshObject(object, gl)
      }
    })
   
    if (assets) {
      setIsReadyRender(true)
    }

    const timerId = setTimeout(()=>{
      setIsReadyAnimate(true)
    }, 500)  

    const modelBoundingBox = new Box3().setFromObject(gltf.scene) 
    const modelCenter = modelBoundingBox.getCenter(new Vector3())
  
    setBoundingBox({
      max: [...modelBoundingBox.max],
      min: [...modelBoundingBox.min],
      center: [...modelCenter],
    })

    return ()=>{
      clearTimeout(timerId)
    }
  }, [gltf, onLoad, viewport])

  useEffect(() => {
    if (rotationAngle && rotationAngle !== rotationAngleState) {
      const radians = -rotationAngle * Math.PI / 180 // Convert angle to radians
      gltf.scene.rotation.y = radians
      setRotation(([prevX]) => [prevX, gltf.scene.rotation.y, 0])
      setRotationAngle(rotationAngle)
    }
  }, [rotationAngle, onLoad])

  useFrame(({ clock }) => {
    if (!gltf?.scene || !camera) return

    // Calculate distance from camera to model center
    const box = new Box3().setFromObject(gltf.scene)
    const center = box.getCenter(new Vector3())
    const distance = camera.position.distanceTo(center)
    const elapsedTime = clock.getElapsedTime() * 1000

    // lowering amount of frame animations rerender
    if (elapsedTime - lastCheck > checkInterval) {
      if (!camera.position.equals(prevPositionRef.current)) {
        const cameraDirection = new Vector3()
        camera.getWorldDirection(cameraDirection)  // Get the camera's direction in world space
        
        gltf.scene.traverse((object) => {
          if (object instanceof Mesh) {
            hideOrShowWallBasedItem({
              meshObject: object,
              assets,
              camera,
              cameraDirection,
              animation,
              setAnimation,
            })
          }
        })
      }
    }

    if (cameraConfig) {
      // Define min/max distances and corresponding opacities
      const minDistance = cameraConfig?.minDistance * 1.65   // fully transparent when very close
      const maxDistance = cameraConfig?.maxDistance   // fully opaque when far
      // Map distance to opacity
      let opacity = (distance - minDistance) / (maxDistance - minDistance)
      setModelOpacity(gltf, opacity)
    }

    lastCheck = elapsedTime
  }, [camera])

  useEffect(() => {
    let startX = 0
    let lastDX = 0
    let isDragging = false
    const dragThreshold = 1
    const dom = gl.domElement
  
    const getClientX = (e) => {
      return e.touches ? e.touches[0].clientX : e.clientX
    }
  
    const handleDown = (e) => {
      // Left mouse button or touch
      if (e.button === 0 || e.touches) {
        isDragging = true
        startX = getClientX(e)
        setAutoRotation(false)
      }
    }

    const handleMove = (e) => {
      e.preventDefault()
      if (!isDragging || !gltf?.scene) return
    
      const currentX = getClientX(e)
      const dx = currentX - startX
    
      if (Math.abs(dx) > dragThreshold) {
        startX = currentX
        const rotationSpeed = 0.003
        lastDX = dx * rotationSpeed
        gltf.scene.rotation.y += lastDX
        setRotation(([prevX]) => [prevX, gltf.scene.rotation.y, 0])
      }
    }

    const handleUp = () => {
      isDragging = false
      if (Math.abs(lastDX) > 0) {
        rotationVelocity.current = lastDX
      } else {
        rotationVelocity.current = 0
      }
      lastDX = 0
    }
  
    dom.addEventListener('pointerdown', handleDown)
    dom.addEventListener('pointermove', handleMove)
    dom.addEventListener('pointerup', handleUp)
    dom.addEventListener('pointercancel', handleUp)
    dom.addEventListener('lostpointercapture', handleUp)

    dom.addEventListener('mousedown', handleDown)
    dom.addEventListener('mousemove', handleMove)
    dom.addEventListener('mouseup', handleUp)
    dom.addEventListener('touchstart', handleDown)
    dom.addEventListener('touchmove', handleMove, false)
    dom.addEventListener('touchend', handleUp)
    dom.addEventListener('contextmenu', (e) => e.preventDefault()) // prevent right-click menu
  
    return () => {
      dom.removeEventListener('mousedown', handleDown)
      dom.removeEventListener('mousemove', handleMove)
      dom.removeEventListener('mouseup', handleUp)
      dom.removeEventListener('touchstart', handleDown)
      dom.removeEventListener('touchmove', handleMove)
      dom.removeEventListener('touchend', handleUp)
      dom.removeEventListener('pointerdown', handleDown)
      dom.removeEventListener('pointermove', handleMove)
      dom.removeEventListener('pointerup', handleUp)
      dom.removeEventListener('pointercancel', handleUp)
      dom.removeEventListener('lostpointercapture', handleUp)
    }
  }, [gltf.scene, gl])

  useEffect(() => {
    const resetViewAction = () => {
      setAutoRotation(true)
    }

    document.addEventListener('reset-view-event', resetViewAction)
    return () => {
      document.removeEventListener('reset-view-event', resetViewAction)
    }
  })

  // Auto move from top to angle
  useFrame((state, delta) => {
    if(isAnimateFromTopView) {
    if (modelRef.current && isReadyAnimate && !isVerticalRotationDone) {
      const targetX = Math.PI / 5;
      const speed = 0.8;

      if (modelRef.current.rotation.x > targetX) {
        modelRef.current.rotation.x -= speed * delta;

        if (modelRef.current.rotation.x <= targetX) {
          modelRef.current.rotation.x = targetX;
          setIsVerticalRotationDone(true);
        }
      }

      setRotation([modelRef.current.rotation.x, modelRef.current.rotation.y, 0]);
    }
    } else {
      setIsVerticalRotationDone(true);
    }
  });

  // Auto-rotate the model
  useFrame((state, delta) => {
    if (autoRotation && modelRef.current && isReadyAnimate && isVerticalRotationDone) {
      modelRef.current.rotation.y += 0.1 * delta // 0.1 radians per second
      setRotation(([prevX]) => [prevX, gltf.scene.rotation.y, 0])
    }

    if (Math.abs(rotationVelocity.current) > 0.0001 && gltf?.scene) {
      gltf.scene.rotation.y += rotationVelocity.current
      rotationVelocity.current *= 0.95 // Dampen velocity gradually
      setRotation(([prevX]) => [prevX, gltf.scene.rotation.y, 0])
    }
  })
  
  return isReadyRender && cameraConfig && <primitive object={gltf.scene} ref={modelRef} />
}
